/**
 * SwitchUI - 开关控制组件
 * 提供可配置的开关控制，支持设置、取值、改变响应事件等方法
 * 样式参考主题样式，使用 vision- 前缀的 CSS 类
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

export interface SwitchOptions extends ElementOptions {
  /** 初始状态 */
  checked?: boolean;
  /** 标签文本 */
  label?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 开关大小 */
  size?: 'sm' | 'md' | 'lg';
  /** 开关颜色主题 */
  theme?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  /** 是否显示标签 */
  showLabel?: boolean;
  /** 标签位置 */
  labelPosition?: 'left' | 'right' | 'top' | 'bottom';
  /** 开关描述文本 */
  description?: string;
  /** 是否只读 */
  readonly?: boolean;
  /** 自定义开关容器样式类 */
  switchContainerClass?: string;
  /** 自定义开关样式类 */
  switchClass?: string;
  /** 自定义标签样式类 */
  labelClass?: string;
  /** 自定义容器样式类 */
  containerClass?: string;
  /** 是否在变化时触发事件 */
  triggerOnChange?: boolean;
}

export class SwitchUI {
  private ui: BootstrapUI;
  private container: HTMLElement;
  private switchElement: HTMLElement;
  private inputElement: HTMLInputElement;
  private sliderElement: HTMLElement;
  private labelElement: HTMLElement | null = null;
  private descriptionElement: HTMLElement | null = null;
  private options: SwitchOptions;
  private onChangeCallbacks: ((checked: boolean) => void)[] = [];
  private isUpdating: boolean = false;

  constructor(options: SwitchOptions = {}) {
    this.ui = bootstrapUI;
    this.options = {
      checked: false,
      label: '开关控制',
      disabled: false,
      size: 'md',
      theme: 'primary',
      showLabel: true,
      labelPosition: 'left',
      description: '',
      readonly: false,
      switchClass: '',
      switchContainerClass: '',
      labelClass: '',
      containerClass: '',
      triggerOnChange: true,
      ...options
    };

    this.container = this.createElement();
    this.switchElement = this.container.querySelector('.vision-switch') as HTMLElement;
    this.inputElement = this.container.querySelector('input[type="checkbox"]') as HTMLInputElement;
    this.sliderElement = this.container.querySelector('.vision-switch-slider') as HTMLElement;
    this.labelElement = this.container.querySelector('.vision-switch-label') as HTMLElement;
    this.descriptionElement = this.container.querySelector('.vision-switch-description') as HTMLElement;

    this.setupEventHandlers();
  }

  /**
   * 创建开关组件元素
   */
  private createElement(): HTMLElement {
    let labeli18nKey = '';
    if(this.options.i18nKey){
      labeli18nKey = this.options.i18nKey;
      this.options.i18nKey = '';
    }
    const container = this.ui.createElement({
      className: [
        'vision-switch-container',
        ...(this.options.containerClass ? [this.options.containerClass] : []),
        `vision-switch-${this.options.size || 'md'}`,
        `vision-switch-${this.options.theme || 'primary'}`,
        `vision-switch-label-${this.options.labelPosition || 'right'}`,
        ...(Array.isArray(this.options.className) ? this.options.className : this.options.className ? [this.options.className] : [])
      ],
      ...this.options
    });

    // 创建标签（如果启用）
    if (this.options.showLabel && this.options.label) {
      const label = this.ui.createElement({
        tag: 'label',
        className: [
          'vision-switch-label',
          ...(this.options.labelClass ? [this.options.labelClass] : []),
          ...(this.options.disabled ? ['disabled'] : [])
        ],
        textContent: this.options.label,
        attributes: {
          for: this.options.id || `switch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        },
        i18nKey: labeli18nKey
      });
      this.labelElement = label;
      container.appendChild(label);
    }

    // 创建开关容器
    const switchContainer = this.ui.createElement({
      className: [
        'vision-switch-wrapper',
        ...(this.options.switchContainerClass ? [this.options.switchContainerClass] : []),
      ]
    });

    // 创建隐藏的复选框输入
    const input = this.ui.createElement({
      tag: 'input',
      className: 'vision-switch-input',
        attributes: {
          type: 'checkbox',
          id: this.options.id || `switch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          ...(this.options.checked && { checked: 'true' }),
          ...(this.options.disabled && { disabled: 'true' }),
          ...(this.options.readonly && { readonly: 'true' })
        }
    }) as HTMLInputElement;

    // 创建开关滑块
    const slider = this.ui.createElement({
      className: [
        'vision-switch',
        ...(this.options.switchClass ? [this.options.switchClass] : []),
        ...(this.options.checked ? ['checked'] : []),
        ...(this.options.disabled ? ['disabled'] : []),
        ...(this.options.readonly ? ['readonly'] : [])
      ]
    });

    // 创建滑块内部元素
    const sliderInner = this.ui.createElement({
      className: 'vision-switch-slider'
    });

    const sliderThumb = this.ui.createElement({
      className: 'vision-switch-thumb'
    });

    sliderInner.appendChild(sliderThumb);
    slider.appendChild(sliderInner);

    switchContainer.appendChild(input);
    switchContainer.appendChild(slider);
    container.appendChild(switchContainer);

    // 创建描述文本（如果有）
    if (this.options.description) {
      const description = this.ui.createElement({
        tag: 'div',
        className: 'vision-switch-description',
        textContent: this.options.description
      });
      this.descriptionElement = description;
      container.appendChild(description);
    }

    return container;
  }

  /**
   * 设置事件处理器
   */
  private setupEventHandlers(): void {
    // 点击事件
    this.ui.on('click', this.switchElement, (e) => {
      if (this.options.disabled || this.options.readonly) {
        e.preventDefault();
        return;
      }
      this.toggle();
    });

    // 输入框变化事件
    this.ui.on('change', this.inputElement, (e) => {
      if (this.isUpdating) return;
      const target = e.target as HTMLInputElement;
      this.updateSwitchState(target.checked);
    });

    // 键盘事件
    this.ui.on('keydown', this.switchElement, (e) => {
      const keyEvent = e as KeyboardEvent;
      if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
        e.preventDefault();
        if (!this.options.disabled && !this.options.readonly) {
          this.toggle();
        }
      }
    });

    // 标签点击事件
    if (this.labelElement) {
      this.ui.on('click', this.labelElement, (e) => {
        if (this.options.disabled || this.options.readonly) {
          e.preventDefault();
          return;
        }
        this.toggle();
      });
    }
  }

  /**
   * 切换开关状态
   */
  private toggle(): void {
    if (this.options.disabled || this.options.readonly) return;
    
    const newState = !this.getChecked();
    this.setChecked(newState);
  }

  /**
   * 更新开关状态
   */
  private updateSwitchState(checked: boolean): void {
    this.isUpdating = true;
    
    // 更新输入框状态
    this.inputElement.checked = checked;
    
    // 更新开关视觉状态
    if (checked) {
      this.switchElement.classList.add('checked');
    } else {
      this.switchElement.classList.remove('checked');
    }
    
    // 更新选项
    this.options.checked = checked;
    
    // 触发变化回调
    if (this.options.triggerOnChange) {
      this.notifyChange(checked);
    }
    
    this.isUpdating = false;
  }

  /**
   * 通知状态变化
   */
  private notifyChange(checked: boolean): void {
    this.onChangeCallbacks.forEach(callback => {
      try {
        callback(checked);
      } catch (error) {
        console.error('开关状态变化回调执行错误:', error);
      }
    });
  }

  /**
   * 获取当前状态
   */
  getChecked(): boolean {
    return this.inputElement.checked;
  }

  /**
   * 设置开关状态
   */
  setChecked(checked: boolean): void {
    if (this.isUpdating) return;
    this.updateSwitchState(checked);
  }

  /**
   * 获取开关值（与 getChecked 相同）
   */
  getValue(): boolean {
    return this.getChecked();
  }

  /**
   * 设置开关值（与 setChecked 相同）
   */
  setValue(value: boolean): void {
    this.setChecked(value);
  }

  /**
   * 是否禁用
   */
  isDisabled(): boolean {
    return this.inputElement.disabled;
  }

  /**
   * 设置禁用状态
   */
  setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    this.inputElement.disabled = disabled;
    
    if (disabled) {
      this.switchElement.classList.add('disabled');
    } else {
      this.switchElement.classList.remove('disabled');
    }
  }

  /**
   * 设置组件激活状态（启用/禁用整个组件）
   */
  setActive(status: boolean): void {
    if (!status) {
      // 禁用整个组件
      this.container.classList.add('forbidden-event-dom');
      
      // 禁用开关
      this.setDisabled(true);
    } else {
      // 启用整个组件
      this.container.classList.remove('forbidden-event-dom');
      
      // 启用开关
      this.setDisabled(false);
    }
  }

  /**
   * 是否只读
   */
  isReadOnly(): boolean {
    return this.inputElement.readOnly;
  }

  /**
   * 设置只读状态
   */
  setReadOnly(readonly: boolean): void {
    this.options.readonly = readonly;
    this.inputElement.readOnly = readonly;
    
    if (readonly) {
      this.switchElement.classList.add('readonly');
    } else {
      this.switchElement.classList.remove('readonly');
    }
  }

  /**
   * 获取标签文本
   */
  getLabel(): string {
    return this.options.label || '';
  }

  /**
   * 设置标签文本
   */
  setLabel(label: string): void {
    this.options.label = label;
    if (this.labelElement) {
      this.labelElement.textContent = label;
    }
  }

  /**
   * 获取描述文本
   */
  getDescription(): string {
    return this.options.description || '';
  }

  /**
   * 设置描述文本
   */
  setDescription(description: string): void {
    this.options.description = description;
    if (this.descriptionElement) {
      this.descriptionElement.textContent = description;
    } else if (description) {
      // 如果描述元素不存在但需要设置描述，创建它
      const descElement = this.ui.createElement({
        tag: 'div',
        className: 'vision-switch-description',
        textContent: description
      });
      this.descriptionElement = descElement;
      this.container.appendChild(descElement);
    }
  }

  /**
   * 获取开关大小
   */
  getSize(): string {
    return this.options.size || 'md';
  }

  /**
   * 设置开关大小
   */
  setSize(size: 'sm' | 'md' | 'lg'): void {
    this.options.size = size;
    
    // 移除旧的大小类
    this.container.classList.remove('vision-switch-sm', 'vision-switch-md', 'vision-switch-lg');
    // 添加新的大小类
    this.container.classList.add(`vision-switch-${size}`);
  }

  /**
   * 获取主题颜色
   */
  getTheme(): string {
    return this.options.theme || 'primary';
  }

  /**
   * 设置主题颜色
   */
  setTheme(theme: 'primary' | 'success' | 'warning' | 'danger' | 'info'): void {
    this.options.theme = theme;
    
    // 移除旧的主题类
    this.container.classList.remove('vision-switch-primary', 'vision-switch-success', 'vision-switch-warning', 'vision-switch-danger', 'vision-switch-info');
    // 添加新的主题类
    this.container.classList.add(`vision-switch-${theme}`);
  }

  /**
   * 添加状态变化监听器
   */
  onChange(callback: (checked: boolean) => void): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * 移除状态变化监听器
   */
  offChange(callback: (checked: boolean) => void): void {
    const index = this.onChangeCallbacks.indexOf(callback);
    if (index > -1) {
      this.onChangeCallbacks.splice(index, 1);
    }
  }

  /**
   * 获取开关元素
   */
  getSwitchElement(): HTMLElement {
    return this.switchElement;
  }

  /**
   * 获取输入框元素
   */
  getInputElement(): HTMLInputElement {
    return this.inputElement;
  }

  /**
   * 获取容器元素
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * 聚焦开关
   */
  focus(): void {
    this.inputElement.focus();
  }

  /**
   * 失焦开关
   */
  blur(): void {
    this.inputElement.blur();
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    this.onChangeCallbacks = [];
    
    // 移除事件监听器
    this.ui.off('click', this.switchElement, () => {});
    this.ui.off('change', this.inputElement, () => {});
    this.ui.off('keydown', this.switchElement, () => {});
    
    if (this.labelElement) {
      this.ui.off('click', this.labelElement, () => {});
    }
    
    // 移除DOM元素
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

// 导出默认实例创建函数
export function createSwitchUI(options: SwitchOptions = {}): SwitchUI {
  return new SwitchUI(options);
}
